import { useEffect, useState } from 'react';

import { PageShell } from '../components/layout/PageShell.js';
import { Stamp } from '../components/ui/Stamp.js';
import { SmartApplyApiError } from '../lib/api-client.js';
import {
  approveSuggestion,
  listAdminSuggestions,
  rejectSuggestion,
  type AdminSuggestion,
} from '../lib/analyses-client.js';
import { useAuth } from '../lib/auth-context.js';
import { useRoute } from '../lib/route.js';

type Filter = 'pending' | 'approved' | 'rejected' | 'all';

export function AdminSuggestionsRoute() {
  const { user } = useAuth();
  const { navigate } = useRoute();
  const [items, setItems] = useState<AdminSuggestion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('pending');

  useEffect(() => {
    if (user && !user.isAdmin) {
      navigate('/');
    }
  }, [user, navigate]);

  async function load() {
    try {
      const res = await listAdminSuggestions();
      setItems(res.items);
    } catch (e) {
      setError(e instanceof SmartApplyApiError ? e.message : 'Falha ao carregar.');
    }
  }

  useEffect(() => {
    void load();
  }, []);

  if (!user?.isAdmin) {
    return (
      <PageShell>
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          acesso restrito...
        </p>
      </PageShell>
    );
  }

  const filtered = items?.filter((s) => filter === 'all' || s.status === filter) ?? null;
  const counts = items
    ? {
        pending: items.filter((s) => s.status === 'pending').length,
        approved: items.filter((s) => s.status === 'approved').length,
        rejected: items.filter((s) => s.status === 'rejected').length,
        all: items.length,
      }
    : null;

  return (
    <PageShell>
      <header className="mb-10">
        <p className="mb-4 inline-block">
          <Stamp variant="suggestion">admin</Stamp>
        </p>
        <h1 className="font-serif text-[36px] font-medium leading-[1.1] tracking-[-0.02em] text-[var(--ink-primary)] sm:text-[40px]">
          Sugestões recebidas
          <span className="text-[var(--accent-saffron)]">.</span>
        </h1>
        <p className="mt-3 max-w-2xl font-sans text-sm text-[var(--ink-secondary)]">
          Avalie sugestões enviadas pelo site público. Aprove pra virar item do roadmap; rejeite com
          nota se for fora do escopo. Quem sugeriu pode ser notificado se deixou e-mail.
        </p>
      </header>

      {error && <p className="mb-6 font-sans text-sm text-[var(--accent-crimson)]">{error}</p>}

      {counts && (
        <div className="mb-8 flex flex-wrap items-baseline gap-2 border-b border-[var(--border-subtle)] pb-4">
          {(['pending', 'approved', 'rejected', 'all'] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className="rounded-sm px-3 py-1 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors"
              style={{
                color: filter === f ? 'var(--ink-primary)' : 'var(--ink-tertiary)',
                backgroundColor:
                  filter === f ? 'var(--bg-paper-recessed)' : 'transparent',
              }}
            >
              {f} ({counts[f]})
            </button>
          ))}
        </div>
      )}

      {items === null && !error && (
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          carregando...
        </p>
      )}

      {filtered && filtered.length === 0 && (
        <p className="font-sans text-base italic text-[var(--ink-tertiary)]">
          Nenhuma sugestão neste filtro.
        </p>
      )}

      {filtered && filtered.length > 0 && (
        <ul className="space-y-4">
          {filtered.map((s) => (
            <SuggestionCard key={s.id} suggestion={s} onChange={() => void load()} />
          ))}
        </ul>
      )}
    </PageShell>
  );
}

function SuggestionCard({
  suggestion,
  onChange,
}: {
  suggestion: AdminSuggestion;
  onChange: () => void;
}) {
  const [mode, setMode] = useState<'idle' | 'approve' | 'reject'>('idle');
  const accent =
    suggestion.status === 'approved'
      ? 'var(--accent-forest)'
      : suggestion.status === 'rejected'
        ? 'var(--accent-crimson)'
        : 'var(--accent-saffron)';

  return (
    <li className="card-paper rounded-sm" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="px-5 py-4">
        <header className="flex flex-wrap items-baseline justify-between gap-3">
          <p className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            {suggestion.status}
            {suggestion.name && ` · ${suggestion.name}`}
            {suggestion.email && ` · ${suggestion.email}`}
          </p>
          <span className="font-mono text-[10px] tabular-nums text-[var(--ink-muted)]">
            {new Date(suggestion.createdAt).toLocaleString('pt-BR')}
          </span>
        </header>

        <p className="mt-3 whitespace-pre-wrap font-serif text-[15px] leading-[1.55] text-[var(--ink-primary)]">
          {suggestion.content}
        </p>

        {suggestion.adminNote && (
          <p className="mt-3 border-l-2 border-[var(--border-default)] pl-3 font-sans text-xs italic text-[var(--ink-tertiary)]">
            nota: {suggestion.adminNote}
          </p>
        )}

        {suggestion.status === 'pending' && mode === 'idle' && (
          <div className="mt-4 flex flex-wrap gap-4">
            <button
              type="button"
              onClick={() => setMode('approve')}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-forest)] hover:underline"
            >
              aprovar → roadmap
            </button>
            <button
              type="button"
              onClick={() => setMode('reject')}
              className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--accent-crimson)] hover:underline"
            >
              rejeitar
            </button>
          </div>
        )}

        {mode === 'approve' && (
          <ApproveForm
            seed={suggestion}
            onCancel={() => setMode('idle')}
            onApproved={() => {
              setMode('idle');
              onChange();
            }}
          />
        )}

        {mode === 'reject' && (
          <RejectForm
            id={suggestion.id}
            onCancel={() => setMode('idle')}
            onRejected={() => {
              setMode('idle');
              onChange();
            }}
          />
        )}
      </div>
    </li>
  );
}

function ApproveForm({
  seed,
  onCancel,
  onApproved,
}: {
  seed: AdminSuggestion;
  onCancel: () => void;
  onApproved: () => void;
}) {
  const [title, setTitle] = useState(seed.content.slice(0, 80));
  const [description, setDescription] = useState(seed.content);
  const [category, setCategory] = useState<
    'ux' | 'ai' | 'integration' | 'performance' | 'billing' | 'other'
  >('other');
  const [status, setStatus] = useState<'considered' | 'planned' | 'in_progress'>(
    'considered',
  );
  const [adminNote, setAdminNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handle() {
    setBusy(true);
    setErr(null);
    try {
      await approveSuggestion(seed.id, {
        title: title.trim(),
        description: description.trim(),
        category,
        status,
        adminNote: adminNote.trim() || undefined,
      });
      onApproved();
    } catch (e) {
      setErr(e instanceof SmartApplyApiError ? e.message : 'Falha ao aprovar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-5 space-y-3 border-t border-[var(--border-subtle)] pt-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
            Título no roadmap
          </span>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={160}
            className="mt-1 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-2 py-1.5 font-sans text-sm"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              Categoria
            </span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value as typeof category)}
              className="mt-1 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-2 py-1.5 font-mono text-xs"
            >
              <option value="ai">ai</option>
              <option value="ux">ux</option>
              <option value="integration">integration</option>
              <option value="performance">performance</option>
              <option value="billing">billing</option>
              <option value="other">other</option>
            </select>
          </label>
          <label className="block">
            <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
              Status inicial
            </span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as typeof status)}
              className="mt-1 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-2 py-1.5 font-mono text-xs"
            >
              <option value="considered">considered</option>
              <option value="planned">planned</option>
              <option value="in_progress">in_progress</option>
            </select>
          </label>
        </div>
      </div>
      <label className="block">
        <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          Descrição (pública no roadmap)
        </span>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          maxLength={2000}
          rows={3}
          className="mt-1 w-full resize-y rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-2 py-1.5 font-sans text-sm"
        />
      </label>
      <label className="block">
        <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          Nota interna (opcional)
        </span>
        <input
          type="text"
          value={adminNote}
          onChange={(e) => setAdminNote(e.target.value)}
          maxLength={500}
          className="mt-1 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-2 py-1.5 font-sans text-sm"
        />
      </label>
      {err && <p className="font-sans text-xs text-[var(--accent-crimson)]">{err}</p>}
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void handle()}
          disabled={busy || !title.trim() || description.trim().length < 20}
          className="rounded-sm bg-[var(--accent-forest)] px-4 py-1.5 font-sans text-sm text-[var(--bg-paper)] disabled:opacity-40"
        >
          {busy ? '...' : 'aprovar'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
        >
          cancelar
        </button>
      </div>
    </div>
  );
}

function RejectForm({
  id,
  onCancel,
  onRejected,
}: {
  id: string;
  onCancel: () => void;
  onRejected: () => void;
}) {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function handle() {
    setBusy(true);
    setErr(null);
    try {
      await rejectSuggestion(id, note.trim() || undefined);
      onRejected();
    } catch (e) {
      setErr(e instanceof SmartApplyApiError ? e.message : 'Falha ao rejeitar.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-5 space-y-3 border-t border-[var(--border-subtle)] pt-4">
      <label className="block">
        <span className="block font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)]">
          Motivo (opcional, fica registrado)
        </span>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          maxLength={500}
          placeholder="ex: viola ToS do LinkedIn, fora de escopo do MVP"
          className="mt-1 w-full rounded-sm border border-[var(--border-default)] bg-[var(--bg-paper)] px-2 py-1.5 font-sans text-sm"
        />
      </label>
      {err && <p className="font-sans text-xs text-[var(--accent-crimson)]">{err}</p>}
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => void handle()}
          disabled={busy}
          className="rounded-sm bg-[var(--accent-crimson)] px-4 py-1.5 font-sans text-sm text-[var(--bg-paper)]"
        >
          {busy ? '...' : 'rejeitar'}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-[var(--ink-tertiary)] hover:text-[var(--ink-primary)]"
        >
          cancelar
        </button>
      </div>
    </div>
  );
}
